/**
 * SyncProgress — rendered as a modal overlay from App.tsx.
 * Shows whenever SyncProgressContext has active data.
 */
import React, { useState } from 'react';
import { RefreshCw, CheckCircle, XCircle, Pause, Play, Square, RotateCcw, X, User, Minimize2 } from 'lucide-react';
import { useSyncProgress } from '../contexts/SyncProgressContext';
import { usePersonDetailContext } from '../contexts/PersonDetailContext';
import { useOperationalSettings } from '../contexts/OperationalSettingsContext';
import { classifyDeviceSyncError } from '../utils/deviceSyncError';
import { syncHeadline } from '../utils/syncProgressPresenter';
import { getAbsoluteImageUrl } from '../utils/imageUrl';

/**
 * Every row of this list is a person being written to a device, so the avatar is the
 * person — their photo, or a person glyph when we have none. It used to fall back to the
 * first letter of `label`, which on a person→devices sync is the DEVICE name: an "F"
 * square for "FACIAL FOGACA", a device wearing a person's slot.
 */
function Avatar({ photoUrl, testId }: { photoUrl?: string; testId: string }) {
  const [failed, setFailed] = useState(false);
  // Relative URLs resolve against file:// in the desktop app, where they load nothing.
  const src = getAbsoluteImageUrl(photoUrl);
  if (src && !failed) {
    return (
      <img
        src={src}
        alt=""
        data-testid={testId}
        onError={() => setFailed(true)}
        className="w-6 h-8 rounded-md object-cover flex-shrink-0 ring-1 ring-black/10"
      />
    );
  }
  return (
    <div
      data-testid={testId}
      className="w-6 h-8 rounded-md bg-gray-100 text-gray-400 flex items-center justify-center flex-shrink-0 ring-1 ring-black/10"
    >
      <User className="w-3.5 h-3.5" />
    </div>
  );
}

export default function SyncProgress() {
  const { data, retryFn, isPaused, isStopped, isExpanded, collapse, pause, resume, stop, close } = useSyncProgress();
  const { openPersonDetail } = usePersonDetailContext();
  const { settings: opSettings } = useOperationalSettings();
  const isDark = opSettings.theme === 'dark';

  // Default surface is the card in the toast corner; this is its zoomed view.
  if (!data || !isExpanded) return null;

  const { zoneName, personPhotoUrl, total, done, ok, fail, recentItems, isComplete } = data;
  const pending  = Math.max(0, total - done);
  const pct      = total > 0 ? Math.round((done / total) * 100) : 0;
  const hasFail  = fail > 0;
  const isRunning = !isComplete && !isStopped;
  // Producers that fan out in parallel cannot stop mid-flight; they opt out of the buttons.
  const canControl = data.controls !== false;

  /* ── panel colours ── */
  const panel   = isDark ? 'bg-gray-900 text-gray-100' : 'bg-white text-gray-900';
  const border  = isDark ? 'border-gray-700' : 'border-gray-200';
  const muted   = isDark ? 'text-gray-400' : 'text-gray-500';
  const divider = isDark ? 'divide-gray-700/60' : 'divide-gray-100';
  const hover   = isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-50';
  const sub     = isDark ? 'bg-gray-800/60' : 'bg-gray-50';

  return (
    /* backdrop */
    <div className="fixed inset-0 z-[200] flex items-center justify-center p-4 bg-black/50 backdrop-blur-sm">

      {/* panel */}
      <div className={`relative w-full max-w-xl rounded-xl shadow-2xl flex flex-col overflow-hidden ${panel}`}
           style={{ maxHeight: '85vh' }}>

        {/* ── header ── */}
        <div className={`flex items-center gap-3 px-5 py-4 border-b ${border} flex-shrink-0`}>
          {personPhotoUrl && <Avatar photoUrl={personPhotoUrl} testId="sync-header-avatar" />}
          <div className="flex-1 min-w-0">
            <p className="text-sm font-semibold leading-tight">
              {syncHeadline({ isComplete, isStopped, ok, fail })}
            </p>
            <p className={`text-xs truncate mt-0.5 ${muted}`}>{zoneName}</p>
          </div>

          {/* controls */}
          <div className="flex items-center gap-1.5 flex-shrink-0">
            {isRunning && canControl && !isPaused && (
              <button onClick={pause}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg border transition-colors
                  ${isDark ? 'border-gray-600 text-gray-300 hover:bg-gray-700' : 'border-gray-200 text-gray-600 hover:bg-gray-50'}`}>
                <Pause className="w-3 h-3" /> Pausar
              </button>
            )}
            {isRunning && canControl && isPaused && (
              <button onClick={resume}
                className={`flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg transition-colors
                  ${isDark ? 'bg-gray-600 text-white hover:bg-gray-500' : 'bg-gray-900 text-white hover:bg-gray-700'}`}>
                <Play className="w-3 h-3" /> Continuar
              </button>
            )}
            {isRunning && canControl && (
              <button onClick={stop}
                className="flex items-center gap-1 px-2.5 py-1.5 text-xs font-medium rounded-lg bg-red-600 text-white hover:bg-red-700 transition-colors">
                <Square className="w-3 h-3" /> Parar
              </button>
            )}
            {/* Back to the card — the run keeps going either way. */}
            <button onClick={collapse} aria-label="Minimizar" title="Minimizar"
              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}>
              <Minimize2 className="w-4 h-4" />
            </button>
            <button onClick={close} aria-label="Fechar"
              className={`p-1.5 rounded-lg transition-colors ${isDark ? 'text-gray-400 hover:bg-gray-700' : 'text-gray-400 hover:bg-gray-100'}`}>
              <X className="w-4 h-4" />
            </button>
          </div>
        </div>

        {/* ── progress ── */}
        <div className={`px-5 py-4 border-b ${border} flex-shrink-0`}>
          <div className="flex items-center justify-between mb-1.5">
            <span className={`text-xs ${muted}`}>{done.toLocaleString()} / {total.toLocaleString()} itens</span>
            <span className="text-xs font-semibold tabular-nums">{pct}%</span>
          </div>
          <div className={`w-full h-1 rounded-full overflow-hidden ${isDark ? 'bg-gray-700' : 'bg-gray-100'}`}>
            <div
              className="h-full rounded-full transition-all duration-300"
              style={{ width: `${pct}%`, backgroundColor: hasFail ? '#f59e0b' : '#96bf13' }}
            />
          </div>

          {isPaused && (
            <div className={`mt-3 flex items-center gap-2 text-xs px-3 py-2 rounded-lg border
              ${isDark ? 'bg-amber-900/20 border-amber-700/40 text-amber-300' : 'bg-amber-50 border-amber-200 text-amber-700'}`}>
              <Pause className="w-3 h-3 flex-shrink-0" />
              Pausado — clique em <strong>Continuar</strong> para retomar.
            </div>
          )}

          {/* stats */}
          <div className="flex items-center gap-5 mt-3">
            <div className="flex items-center gap-1.5">
              <CheckCircle className="w-3.5 h-3.5 text-green-500" />
              <span className={`text-xs ${muted}`}>Sucesso</span>
              <span className="text-sm font-bold text-green-500 tabular-nums">{ok.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <XCircle className="w-3.5 h-3.5 text-red-400" />
              <span className={`text-xs ${muted}`}>Falhas</span>
              <span className="text-sm font-bold text-red-400 tabular-nums">{fail.toLocaleString()}</span>
            </div>
            <div className="flex items-center gap-1.5">
              <RefreshCw className={`w-3.5 h-3.5 ${muted} ${isRunning && !isPaused ? 'animate-spin' : ''}`} />
              <span className={`text-xs ${muted}`}>Na fila</span>
              <span className="text-sm font-bold tabular-nums">{pending.toLocaleString()}</span>
            </div>
          </div>
        </div>

        {/* ── list header ── */}
        <div className={`flex items-center justify-between px-5 py-2.5 border-b ${border} flex-shrink-0 ${sub}`}>
          <span className={`text-[11px] font-semibold uppercase tracking-wide ${muted}`}>
            Itens processados ({recentItems.length})
          </span>
          {isRunning && (
            <span className="flex items-center gap-1 text-[11px] text-[#96bf13]">
              <RefreshCw className="w-2.5 h-2.5 animate-spin" /> Em andamento
            </span>
          )}
        </div>

        {/* ── scrollable list ── */}
        <div className="overflow-y-auto flex-1">
          {recentItems.length === 0 ? (
            <div className={`flex flex-col items-center justify-center py-10 gap-2 ${muted}`}>
              <RefreshCw className={`w-5 h-5 opacity-30 ${isRunning ? 'animate-spin' : ''}`} />
              <p className="text-xs">Aguardando itens...</p>
            </div>
          ) : (
            <ul className={`divide-y ${divider}`}>
              {[...recentItems].reverse().map((item, idx) => (
                <li
                  key={idx}
                  className={`flex items-center gap-3 px-5 py-2.5 transition-colors
                    ${item.personId ? `cursor-pointer ${hover}` : ''}`}
                  onClick={() => item.personId && openPersonDetail(item.personId)}
                >
                  <Avatar photoUrl={item.photoUrl} testId="sync-person-avatar" />
                  <div className="min-w-0 flex-1">
                    <p className={`text-sm font-medium truncate ${item.personId ? 'text-blue-500' : ''}`}>
                      {item.label || '—'}
                    </p>
                    {item.owner && <p className={`text-xs truncate ${muted}`}>{item.owner}</p>}
                    {item.note && <p className={`text-xs truncate ${muted}`}>{item.note}</p>}
                    {/* Single chokepoint: no producer has to remember to humanise, and the
                        support code matches the one the toast showed for the same failure. */}
                    {item.error && (() => {
                      const { code, message } = classifyDeviceSyncError(item.error);
                      return (
                        <p className="text-xs text-red-400 mt-0.5 line-clamp-2">
                          {message}
                          <span className={`ml-1.5 font-mono text-[10px] ${muted}`}>{code}</span>
                        </p>
                      );
                    })()}
                  </div>
                  {item.status === 'ok'
                    ? <CheckCircle className="w-4 h-4 text-green-500 flex-shrink-0" />
                    : <XCircle    className="w-4 h-4 text-red-400   flex-shrink-0" />}
                </li>
              ))}
            </ul>
          )}
        </div>

        {/* ── footer ──
            No "Fechar" here: the header already has an X, and a full-width bar under a
            117-row list only pushed the last rows out of view. Retry is the one action
            that belongs at the end. */}
        {isComplete && retryFn && hasFail && (
          <div className={`flex items-center px-5 py-4 border-t ${border} flex-shrink-0`}>
            <button onClick={retryFn}
              className={`flex items-center justify-center gap-2 w-full px-4 py-2.5 rounded-lg text-sm font-medium border transition-colors
                ${isDark ? 'border-amber-700 text-amber-400 hover:bg-amber-900/30' : 'border-amber-300 text-amber-700 hover:bg-amber-50'}`}>
              <RotateCcw className="w-3.5 h-3.5" />
              Tentar novamente ({fail})
            </button>
          </div>
        )}
      </div>
    </div>
  );
}
